export interface SupabaseEdgeFetchConfig {
  /** Full edge-function root URL (no trailing slash).
   *  Local:  `/supabase-functions/root`
   *  Remote: `https://xxx.supabase.co/functions/v1/root`
   */
  baseUrl: string;
  /** Resolves the Auth0/Supabase access token used as the Bearer credential. */
  getToken: () => Promise<string>;
}

export interface SupabaseEdgeRequestOptions {
  method?: string;
  body?: unknown;
}

/** Error thrown for a non-2xx edge response; carries the status and the optional machine `code`. */
export class EdgeFetchError extends Error {
  constructor(message: string, readonly status: number, readonly code?: string) {
    super(message);
    this.name = 'EdgeFetchError';
  }
}

/**
 * Build a typed fetch helper for Supabase Edge Functions.
 *
 * `baseUrl` must already point at the function root (the value returned by
 * `getAuthBackendUrl()`). Endpoint segments are appended directly:
 * `${baseUrl}/${endpoint}`. Both cookie sessions (via `credentials: 'include'`)
 * and token sessions (via `Authorization: Bearer`) are supported.
 *
 * @example
 * ```ts
 * const supabaseEdgeFetch = createSupabaseEdgeFetch({
 *   baseUrl: getAuthBackendUrl(),
 *   getToken: () => getAccessToken(),
 * });
 *
 * const { connectors } = await supabaseEdgeFetch<ConnectorsListResponse>(
 *   'connectors'
 * );
 * ```
 */
export function createSupabaseEdgeFetch(config: SupabaseEdgeFetchConfig) {
  return async function supabaseEdgeFetch<T>(endpoint: string, options?: SupabaseEdgeRequestOptions): Promise<T> {
    const token = await config.getToken();
    const response = await fetch(`${config.baseUrl}/${endpoint}`, {
      method: options?.method ?? 'GET',
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: options?.body ? JSON.stringify(options.body) : undefined,
    });

    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as { error?: string; code?: string };
      throw new EdgeFetchError(body.error || `HTTP ${response.status}`, response.status, body.code);
    }

    return response.json() as Promise<T>;
  };
}

export type SupabaseEdgeFetch = ReturnType<typeof createSupabaseEdgeFetch>;
